import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, Shield, History, Save, RotateCcw, 
  CheckSquare, Search, Eye, Plus, Edit, Trash2, 
  XCircle, CheckCircle, FileText, UserCog
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const ACTION_LABELS = {
  view: { label: 'Görüntüle', icon: Eye, color: 'bg-blue-100 text-blue-700' },
  create: { label: 'Ekle', icon: Plus, color: 'bg-green-100 text-green-700' },
  edit: { label: 'Düzenle', icon: Edit, color: 'bg-yellow-100 text-yellow-700' },
  delete: { label: 'Sil', icon: Trash2, color: 'bg-red-100 text-red-700' },
  cancel: { label: 'İptal', icon: XCircle, color: 'bg-orange-100 text-orange-700' },
  approve: { label: 'Onayla', icon: CheckCircle, color: 'bg-purple-100 text-purple-700' },
  export: { label: 'Dışa Aktar', icon: FileText, color: 'bg-gray-100 text-gray-700' },
};

const GROUP_LABELS = {
  ana: 'Ana Sayfa',
  stok: 'Stok Yönetimi',
  sevkiyat: 'Sevkiyat',
  satin_alma: 'Satın Alma',
  satis: 'Satış & Mutfak',
  finans: 'Finans & Muhasebe',
  yonetim: 'Yönetim',
};

const ROLE_LABELS = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700' },
  manager: { label: 'Müdür', color: 'bg-blue-100 text-blue-700' },
  staff: { label: 'Personel', color: 'bg-gray-100 text-gray-700' },
};

export default function UserPermissions() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [menuDefinitions, setMenuDefinitions] = useState({ modules: {}, groups: {} });
  const [templates, setTemplates] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User selection state
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState({});
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);
  const [hasCustomPermissions, setHasCustomPermissions] = useState(false);
  const [permissionsChanged, setPermissionsChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Search
  const [userSearch, setUserSearch] = useState('');
  
  // Template dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [usersRes, definitionsRes, templatesRes] = await Promise.all([
        api.get('/users'),
        api.get('/permissions/menu-definitions'),
        api.get('/permissions/templates'),
      ]);
      setUsers(usersRes.data);
      setMenuDefinitions(definitionsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (userId = null) => {
    try {
      const url = userId ? `/permissions/audit-log?target_user_id=${userId}` : '/permissions/audit-log';
      const res = await api.get(url);
      setAuditLogs(res.data);
    } catch (error) {
      console.error('Audit log error:', error);
    }
  };

  const selectUser = async (userId) => {
    try {
      const res = await api.get(`/permissions/user/${userId}`);
      setSelectedUserId(userId);
      setSelectedUserInfo(res.data);
      setSelectedUserPermissions(res.data.permissions || {});
      setHasCustomPermissions(res.data.has_custom_permissions);
      setPermissionsChanged(false);
    } catch (error) {
      toast.error('Kullanıcı yetkileri yüklenemedi');
    }
  };

  const handlePermissionChange = (moduleKey, action, checked) => {
    setSelectedUserPermissions(prev => {
      const newPermissions = { ...prev };
      if (!newPermissions[moduleKey]) {
        newPermissions[moduleKey] = { actions: [] };
      }
      
      const actions = newPermissions[moduleKey].actions || [];
      if (checked) {
        if (!actions.includes(action)) {
          newPermissions[moduleKey] = { actions: [...actions, action] };
        }
      } else {
        newPermissions[moduleKey] = { actions: actions.filter(a => a !== action) };
      }
      
      // Remove module if no actions
      if (newPermissions[moduleKey].actions.length === 0) {
        delete newPermissions[moduleKey];
      }
      
      return newPermissions;
    });
    setPermissionsChanged(true);
  };

  const handleModuleToggle = (moduleKey, actions, checked) => {
    setSelectedUserPermissions(prev => {
      const newPermissions = { ...prev };
      if (checked) {
        newPermissions[moduleKey] = { actions: [...actions] };
      } else {
        delete newPermissions[moduleKey];
      }
      return newPermissions;
    });
    setPermissionsChanged(true);
  };

  const handleSelectAll = () => {
    const allPermissions = {};
    Object.entries(menuDefinitions.modules).forEach(([moduleKey, moduleDef]) => {
      allPermissions[moduleKey] = { actions: [...moduleDef.actions] };
    });
    setSelectedUserPermissions(allPermissions);
    setPermissionsChanged(true);
  };

  const handleDeselectAll = () => {
    setSelectedUserPermissions({});
    setPermissionsChanged(true);
  };

  const savePermissions = async () => {
    if (!selectedUserId) return;
    
    try {
      setSaving(true);
      await api.put(`/permissions/user/${selectedUserId}`, {
        permissions: selectedUserPermissions,
      });
      toast.success('Yetkiler kaydedildi');
      setPermissionsChanged(false);
      setHasCustomPermissions(true);
      fetchAuditLogs(selectedUserId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yetkiler kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!selectedUserId) return;
    
    try {
      setSaving(true);
      await api.post(`/permissions/user/${selectedUserId}/reset`);
      toast.success('Yetkiler varsayılana döndürüldü');
      await selectUser(selectedUserId);
      fetchAuditLogs(selectedUserId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yetkiler sıfırlanamadı');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async () => {
    if (!selectedUserId || !selectedTemplate) return;
    
    try {
      setSaving(true);
      await api.post(`/permissions/user/${selectedUserId}/apply-template?template_key=${selectedTemplate}`);
      toast.success('Şablon uygulandı');
      await selectUser(selectedUserId);
      fetchAuditLogs(selectedUserId);
      setTemplateDialogOpen(false);
      setSelectedTemplate('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablon uygulanamadı');
    } finally {
      setSaving(false);
    }
  };

  // Group modules by their group
  const groupedModules = Object.entries(menuDefinitions.modules || {}).reduce((acc, [key, module]) => {
    const group = module.group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push({ key, ...module });
    return acc;
  }, {});

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const selectedUser = users.find(u => u.id === selectedUserId);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="permissions-title">
              Kullanıcılar ve Yetkiler
            </h1>
            <p className="text-muted-foreground">
              Kullanıcı bazlı erişim yetkilerini yönetin
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" />
              Kullanıcı Yetkileri
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <Shield className="w-4 h-4 mr-2" />
              Rol Şablonları
            </TabsTrigger>
            <TabsTrigger value="audit" onClick={() => fetchAuditLogs()} data-testid="audit-tab">
              <History className="w-4 h-4 mr-2" />
              Değişiklik Geçmişi
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* User List */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Kullanıcılar</CardTitle>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Kullanıcı ara..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9"
                      data-testid="user-search"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-2">
                      {filteredUsers.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => selectUser(u.id)}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedUserId === u.id 
                              ? 'bg-primary/10 border border-primary' 
                              : 'hover:bg-muted border border-transparent'
                          }`}
                          data-testid={`user-item-${u.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                            <Badge className={ROLE_LABELS[u.role]?.color || 'bg-gray-100'}>
                              {ROLE_LABELS[u.role]?.label || u.role}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Permissions Editor */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {selectedUser ? `${selectedUser.name} - Yetkiler` : 'Kullanıcı Seçin'}
                      </CardTitle>
                      {selectedUserInfo && (
                        <CardDescription>
                          {hasCustomPermissions ? (
                            <Badge variant="outline" className="mt-1">Özel yetkiler tanımlı</Badge>
                          ) : (
                            <Badge variant="secondary" className="mt-1">Rol bazlı varsayılan</Badge>
                          )}
                        </CardDescription>
                      )}
                    </div>
                    {selectedUserId && (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleSelectAll}
                          data-testid="select-all-btn"
                        >
                          <CheckSquare className="w-4 h-4 mr-1" />
                          Tümünü Seç
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setTemplateDialogOpen(true)}
                          data-testid="apply-template-btn"
                        >
                          <UserCog className="w-4 h-4 mr-1" />
                          Şablon Uygula
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!selectedUserId ? (
                    <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                      <Users className="w-12 h-12 mb-4 opacity-50" />
                      <p>Yetkileri düzenlemek için bir kullanıcı seçin</p>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-[400px] pr-4">
                        <Accordion type="multiple" defaultValue={Object.keys(groupedModules)} className="space-y-2">
                          {Object.entries(groupedModules).map(([groupKey, modules]) => (
                            <AccordionItem key={groupKey} value={groupKey} className="border rounded-lg px-3">
                              <AccordionTrigger className="hover:no-underline">
                                <span className="font-medium">
                                  {GROUP_LABELS[groupKey] || groupKey}
                                </span>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="space-y-3 pt-2">
                                  {modules.map((module) => {
                                    const modulePerms = selectedUserPermissions[module.key];
                                    const moduleActions = modulePerms?.actions || [];
                                    const allSelected = module.actions.every(a => moduleActions.includes(a));
                                    const someSelected = module.actions.some(a => moduleActions.includes(a));
                                    
                                    return (
                                      <div key={module.key} className="border-b last:border-0 pb-3 last:pb-0">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <Checkbox
                                              checked={allSelected}
                                              onCheckedChange={(checked) => 
                                                handleModuleToggle(module.key, module.actions, checked)
                                              }
                                              data-testid={`module-checkbox-${module.key}`}
                                            />
                                            <span className="font-medium">{module.name}</span>
                                          </div>
                                          <Badge variant="outline" className="text-xs">
                                            Min: {module.min_role}
                                          </Badge>
                                        </div>
                                        <div className="flex flex-wrap gap-2 ml-6">
                                          {module.actions.map((action) => {
                                            const actionInfo = ACTION_LABELS[action];
                                            const isChecked = moduleActions.includes(action);
                                            const Icon = actionInfo?.icon || Eye;
                                            
                                            return (
                                              <label
                                                key={action}
                                                className={`flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer transition-colors ${
                                                  isChecked 
                                                    ? actionInfo?.color || 'bg-primary/10' 
                                                    : 'bg-muted hover:bg-muted/80'
                                                }`}
                                              >
                                                <Checkbox
                                                  checked={isChecked}
                                                  onCheckedChange={(checked) => 
                                                    handlePermissionChange(module.key, action, checked)
                                                  }
                                                  data-testid={`action-checkbox-${module.key}-${action}`}
                                                />
                                                <Icon className="w-3.5 h-3.5" />
                                                <span className="text-sm">{actionInfo?.label || action}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </ScrollArea>

                      {/* Action Buttons */}
                      <div className="flex justify-between items-center pt-4 border-t mt-4">
                        <Button
                          variant="outline"
                          onClick={resetToDefault}
                          disabled={saving || !hasCustomPermissions}
                          data-testid="reset-permissions-btn"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Varsayılana Döndür
                        </Button>
                        <Button
                          onClick={savePermissions}
                          disabled={saving || !permissionsChanged}
                          data-testid="save-permissions-btn"
                        >
                          {saving ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Kaydet
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(templates).map(([key, template]) => (
                <Card key={key}>
                  <CardHeader>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Dahil Modüller ({Object.keys(template.permissions).length})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(template.permissions).slice(0, 8).map((moduleKey) => (
                          <Badge key={moduleKey} variant="secondary" className="text-xs">
                            {menuDefinitions.modules?.[moduleKey]?.name || moduleKey}
                          </Badge>
                        ))}
                        {Object.keys(template.permissions).length > 8 && (
                          <Badge variant="outline" className="text-xs">
                            +{Object.keys(template.permissions).length - 8} daha
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle>Yetki Değişiklik Geçmişi</CardTitle>
                <CardDescription>
                  Kullanıcı yetkilerinde yapılan tüm değişiklikler
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Hedef Kullanıcı</TableHead>
                      <TableHead>Değiştiren</TableHead>
                      <TableHead>İşlem</TableHead>
                      <TableHead>Şablon</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Henüz değişiklik kaydı yok
                        </TableCell>
                      </TableRow>
                    ) : (
                      auditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {new Date(log.timestamp).toLocaleString('tr-TR')}
                          </TableCell>
                          <TableCell>{log.target_user_name}</TableCell>
                          <TableCell>{log.changed_by_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {log.change_type === 'create' && 'Oluşturuldu'}
                              {log.change_type === 'update' && 'Güncellendi'}
                              {log.change_type === 'reset' && 'Sıfırlandı'}
                              {log.change_type === 'template_apply' && 'Şablon Uygulandı'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.template_used ? (
                              <Badge>{templates[log.template_used]?.name || log.template_used}</Badge>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Template Apply Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Şablon Uygula</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Seçilen şablon kullanıcının mevcut yetkilerinin üzerine yazılacaktır.
              </p>
              <div className="space-y-2">
                <Label>Şablon Seçin</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Şablon seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(templates).map(([key, template]) => (
                      <SelectItem key={key} value={key}>
                        {template.name} - {template.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={applyTemplate} disabled={!selectedTemplate || saving}>
                Uygula
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
